import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Inbox as InboxIcon, Archive, AlertTriangle, FolderInput } from "lucide-react";

/**
 * Unified "Move to…" dropdown.
 *
 * Renders only the valid destinations for the current folder:
 *   inbox / starred / sent →  Archive, Spam
 *   archive               →  Inbox (= unarchive), Spam
 *   spam                  →  Inbox (= not_spam), Archive
 *
 * The dropdown emits the existing per-folder action names so it plugs into
 * every caller's existing `handleAction(action)` handler with no backend
 * changes:
 *
 *   target  | from inbox  | from archive | from spam
 *   --------+-------------+--------------+-----------
 *   inbox   |  (n/a)      |  unarchive   |  not_spam
 *   archive |  archive    |   (n/a)      |  archive
 *   spam    |  spam       |  spam        |   (n/a)
 *
 * Props:
 *   folder      : current folder context
 *   onAction    : (actionName: string) => void
 *   variant     : "icon" (icon-only square button) | "button" (text + chevron)
 *                 | "menuitem" (renders bare DropdownMenuItem entries — caller
 *                   provides their own outer DropdownMenu)
 *   testIdPrefix: data-testid prefix for the trigger + items
 *   disabled    : forwarded to the trigger
 *   size        : forwarded to the trigger ("sm" by default)
 */
export const MOVE_TARGETS = ["inbox", "archive", "spam"];

function buildOptions(folder) {
  const out = [];
  const f = String(folder || "").toLowerCase();
  if (f === "spam") {
    out.push({ key: "inbox",   action: "not_spam",  label: "Inbox",   Icon: InboxIcon });
    out.push({ key: "archive", action: "archive",   label: "Archive", Icon: Archive });
  } else if (f === "archive" || f === "archived") {
    out.push({ key: "inbox", action: "unarchive", label: "Inbox", Icon: InboxIcon });
    out.push({ key: "spam",  action: "spam",      label: "Spam",  Icon: AlertTriangle });
  } else {
    // inbox, starred, sent (treated like inbox), or unknown → default targets
    out.push({ key: "archive", action: "archive", label: "Archive", Icon: Archive });
    out.push({ key: "spam",    action: "spam",    label: "Spam",    Icon: AlertTriangle });
  }
  return out;
}

export default function MoveToMenu({
  folder, onAction,
  variant = "icon",
  testIdPrefix = "move-to",
  disabled = false,
  size = "sm",
}) {
  const options = buildOptions(folder);
  if (!options.length) return null;

  const items = options.map(({ key, action, label, Icon }) => (
    <DropdownMenuItem
      key={key}
      onClick={() => onAction(action)}
      data-testid={`${testIdPrefix}-${key}`}
    >
      <Icon size={14} className="mr-2" /> {label}
    </DropdownMenuItem>
  ));

  if (variant === "menuitem") {
    // Caller provides outer dropdown — we just emit the items.
    return <>{items}</>;
  }

  const trigger =
    variant === "button" ? (
      <Button
        size={size}
        variant="outline"
        className="h-7 text-xs gap-1"
        disabled={disabled}
        data-testid={`${testIdPrefix}-trigger`}
        title="Move to…"
        aria-label="Move to"
      >
        <FolderInput size={13} /> Move to…
      </Button>
    ) : (
      <Button
        variant="ghost"
        size={size}
        className="text-[#555555] hover:text-[#111111] h-9 w-9 p-0"
        disabled={disabled}
        data-testid={`${testIdPrefix}-trigger`}
        title="Move to…"
        aria-label="Move to"
      >
        <FolderInput size={16} />
      </Button>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start">{items}</DropdownMenuContent>
    </DropdownMenu>
  );
}
